import type { EnvVar } from '@/api/schemas';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { EnvVarActions } from './EnvVarsTable';

export function RowMenu({ v, onEdit, onNewVersion, onReveal, onVersions, onDelete }: { v: EnvVar } & EnvVarActions) {
  const readOnly = v.inherited === true;
  return (
    <DropdownMenu>
      <RowMenuTrigger v={v} />
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem data-testid="env-action-new-version" disabled={readOnly} onSelect={() => onNewVersion(v)}>
          <Icon name="upload" size={18} className="text-muted-foreground" />
          New version
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="env-action-edit" disabled={readOnly} onSelect={() => onEdit(v)}>
          <Icon name="tune" size={18} className="text-muted-foreground" />
          Edit metadata
        </DropdownMenuItem>
        <RevealItem v={v} onReveal={onReveal} />
        <DropdownMenuItem data-testid="env-action-versions" onSelect={() => onVersions(v)}>
          <Icon name="history" size={18} className="text-muted-foreground" />
          Versions
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="env-action-delete" variant="destructive" disabled={readOnly} onSelect={() => onDelete(v)}>
          <Icon name="delete" size={18} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RowMenuTrigger({ v }: { v: EnvVar }) {
  return (
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${v.key}`} data-testid={`env-menu-${v.key}`} className="text-subtle">
        <Icon name="more_vert" size={20} />
      </Button>
    </DropdownMenuTrigger>
  );
}

function RevealItem({ v, onReveal }: { v: EnvVar; onReveal: (v: EnvVar) => void }) {
  const isSecret = v.visibility === 'secret';
  return (
    <DropdownMenuItem data-testid="env-action-reveal" disabled={isSecret} title={isSecret ? 'Secret values are never revealable' : undefined} onSelect={() => onReveal(v)}>
      <Icon name={isSecret ? 'lock' : 'visibility'} size={18} className="text-muted-foreground" />
      Reveal value
    </DropdownMenuItem>
  );
}
